import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import VerifyOtp from "../helpers/verifyOtp";
import { useDispatch } from "react-redux";
import { setShowPreloader } from "../store/activePage";
import ResendOtp from "../helpers/resendOtp";
import { useNavigate } from "react-router-dom";
import { FaCheckCircle, FaRedo, FaShieldAlt } from "react-icons/fa";

const inputClass = (hasError) =>
  `w-full rounded-lg border bg-white px-4 py-3 text-center text-lg font-bold tracking-[0.35em] text-slate-900 outline-none transition placeholder:text-sm placeholder:font-medium placeholder:normal-case placeholder:tracking-normal placeholder:text-slate-400 ${
    hasError
      ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100"
      : "border-slate-200 focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
  }`;

const OTPScreen = () => {
  const Logo = require("../assets/images/logo.jpeg");
  const otpValidationSchema = Yup.object().shape({
    otp: Yup.string()
      .length(6, "OTP must be exactly 6 digits")
      .matches(/^\d{6}$/, "OTP must only contain numbers")
      .required("OTP is required"),
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [resendTimeout, setResendTimeout] = useState(0);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const savedTimeout = localStorage.getItem("otpResendTimeout");
    if (savedTimeout) {
      const remainingTime = Math.max(
        0,
        60 - Math.floor((Date.now() - parseInt(savedTimeout, 10)) / 1000),
      );
      setResendTimeout(remainingTime);
    }
  }, []);

  useEffect(() => {
    if (resendTimeout <= 0) return;

    const interval = setInterval(() => {
      setResendTimeout((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          localStorage.removeItem("otpResendTimeout");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendTimeout]);

  const handleSubmit = async (values) => {
    console.log("Submitted OTP:", values.otp);
    dispatch(setShowPreloader(true));
    const otpVer = await VerifyOtp(values.otp, navigate, dispatch);
    dispatch(setShowPreloader(false));
    console.log("otpVer", otpVer);
  };

  const HandleResendOtp = async () => {
    if (resendTimeout > 0 || isResending) return;

    setIsResending(true);
    dispatch(setShowPreloader(true));
    try {
      await ResendOtp();
      setResendTimeout(60);
      localStorage.setItem("otpResendTimeout", Date.now().toString());
    } catch (error) {
      console.error("Error resending OTP:", error);
    } finally {
      dispatch(setShowPreloader(false));
      setIsResending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[linear-gradient(180deg,#eef4f8_0%,#f8fbfd_45%,#eef5f7_100%)] px-4 py-10 sm:px-6">
      <div className="w-full max-w-md">
        <div className="mb-7 flex items-center justify-center gap-3">
          <img
            src={Logo}
            alt="Digital Moi Tech"
            className="h-12 w-12 rounded-xl object-cover ring-2 ring-teal-200"
          />
          <div>
            <p className="font-bold text-slate-950">Digital Moi Tech</p>
            <p className="text-sm font-semibold text-teal-700">
              OTP Verification
            </p>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/70 sm:p-8">
          <div className="mb-7 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-teal-50 text-teal-700 ring-1 ring-teal-100">
              <FaShieldAlt size={24} />
            </div>
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-teal-700">
              Secure verification
            </p>
            <h1 className="mt-2 text-3xl font-black tracking-tight text-slate-950">
              Enter OTP
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-500">
              Please enter the 6-digit OTP sent to your registered mobile number.
            </p>
          </div>

          <Formik
            initialValues={{ otp: "" }}
            validationSchema={otpValidationSchema}
            onSubmit={handleSubmit}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <div className="mb-4">
                  <label
                    htmlFor="otp"
                    className="mb-1.5 block text-sm font-bold text-slate-700"
                  >
                    OTP
                  </label>
                  <Field
                    id="otp"
                    name="otp"
                    type="text"
                    className={inputClass(errors.otp && touched.otp)}
                    placeholder="Enter 6-digit OTP"
                  />
                  {errors.otp && touched.otp && (
                    <div className="mt-1.5 text-xs font-semibold text-red-600">
                      {errors.otp}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  className={`mt-2 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold text-white transition hover:bg-teal-800 ${
                    isSubmitting ? "cursor-not-allowed opacity-60" : ""
                  }`}
                  disabled={isSubmitting}
                  style={{
                    background:
                      "linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)",
                    boxShadow: "0 18px 32px rgba(20,184,166,0.18)",
                  }}
                >
                  <FaCheckCircle className="text-xs" />
                  {isSubmitting ? "Verifying..." : "Verify OTP"}
                </button>
              </Form>
            )}
          </Formik>
          <div className="mt-6">
            <button
              onClick={HandleResendOtp}
              disabled={resendTimeout > 0 || isResending}
              className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-bold transition ${
                resendTimeout > 0 || isResending
                  ? "cursor-not-allowed bg-slate-100 text-slate-500"
                  : "border border-teal-200 bg-teal-50 text-teal-800 hover:bg-teal-100"
              }`}
            >
              <FaRedo className="text-xs" />
              {resendTimeout > 0
                ? `Resend OTP in ${resendTimeout}s`
                : isResending
                ? "Sending..."
                : "Resend OTP"}
            </button>
            {resendTimeout > 0 && (
              <p className="mt-2 text-center text-xs font-medium text-slate-500">
                Please wait before requesting another OTP
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPScreen;
